import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';

import { DsTypography } from 'componentsV2/DS';

import {
  OFFER_COUNTDOWN_TEXTS,
  OFFER_REMAINING_SLOTS_TEXTS,
} from '../../constants/texts';
import { formatCountdown } from '../../utils/formatCountdown';

function ClockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="3" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M15.5 6.3a3 3 0 0 1 0 5.4M17 14.3c2.3.5 4 2.2 4 4.7" />
    </svg>
  );
}

/**
 * FIN-8011/FIN-8239: bloco do card do resumo, acima do nome do produto: pill
 * "Oferta limitada" e, abaixo, as linhas "Encerra em" (contador) e "Vagas
 * restantes", só as que se aplicam, seguidas de um divisor. Sem nenhuma
 * linha, não renderiza. Ao zerar o contador, a linha some; o pagamento
 * continua possível (o /v3 decide). A distância do divisor ao conteúdo
 * seguinte é de 17px (design); `dividerClassName` troca a margem inferior
 * para o consumidor descontar o espaço que o contêiner dele já soma.
 */
export function OfferSummaryUrgency({
  remainingMs,
  remainingSlots,
  dividerClassName = 'mb-[17px]',
}: {
  remainingMs: number | null;
  remainingSlots?: number | null;
  /** Margem inferior do divisor (padrão 17px, sem espaço extra do contêiner). */
  dividerClassName?: string;
}) {
  // Sem provider (testes), cai na variável CSS do DS.
  const theme = useContext(ThemeContext);

  const hasCountdown = remainingMs !== null;
  const hasSlots = remainingSlots !== null && remainingSlots !== undefined;

  if (!hasCountdown && !hasSlots) return null;

  const formattedRemaining = hasCountdown ? formatCountdown(remainingMs) : '';

  return (
    <div className="w-full" data-testid="offer-summary-urgency">
      {/* Cores da marca, como a etiqueta "Mais popular" do ProductCard: fundo
          secondary-pure e texto neutral-button (o contraste do botão). */}
      <span
        className="inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-bold leading-5"
        style={{
          backgroundColor:
            theme?.color?.secondary?.pure ?? 'var(--color-secondary-pure)',
          color: theme?.color?.neutral?.button ?? 'var(--neutral-color-button)',
        }}
      >
        {OFFER_COUNTDOWN_TEXTS.limitedBadge}
      </span>

      <div className="mt-[11px] flex flex-col gap-[7px]">
        {hasCountdown && (
          <div
            className="flex h-5 items-center justify-between gap-2"
            data-testid="offer-summary-countdown"
            role="timer"
            aria-label={`${OFFER_COUNTDOWN_TEXTS.label} ${formattedRemaining}`}
          >
            <span className="flex items-center gap-2 leading-5">
              <ClockIcon />
              <DsTypography
                component="paragraph"
                size="small"
                color="neutral-pure"
              >
                {OFFER_COUNTDOWN_TEXTS.label}
              </DsTypography>
            </span>
            <span className="whitespace-nowrap">
              <DsTypography
                component="paragraph"
                size="small-bold"
                color="secondary-pure"
              >
                {formattedRemaining}
              </DsTypography>
            </span>
          </div>
        )}
        {hasSlots && (
          <div
            className="flex h-5 items-center justify-between gap-2"
            data-testid="offer-summary-slots"
          >
            <span className="flex items-center gap-2 leading-5">
              <PeopleIcon />
              <DsTypography
                component="paragraph"
                size="small"
                color="neutral-pure"
              >
                {OFFER_REMAINING_SLOTS_TEXTS.summaryLabel(remainingSlots)}
              </DsTypography>
            </span>
            <span className="whitespace-nowrap">
              <DsTypography
                component="paragraph"
                size="small-bold"
                color="secondary-pure"
              >
                {remainingSlots}
              </DsTypography>
            </span>
          </div>
        )}
      </div>

      <div
        className={`mt-[15px] h-px w-full ${dividerClassName}`}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        aria-hidden="true"
      />
    </div>
  );
}
